import { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../../ui/table";
import Badge from "../../ui/badge/Badge";
import { Trash2, AlertCircle, RefreshCw, CheckCircle, XCircle, Copy } from 'lucide-react';
import MoreButton from '../../common/MoreButton';
import { useNavigate } from "react-router";
import api from "../../../services/api";
import authService from "../../../services/authService";
import Pagination from "../../ui/pagination/Pagination";
import ComponentCard from "../../common/ComponentCard";
import Button from "../../ui/button/Button";
import Modal from '../../common/Modal';
import Alert from '../../ui/alert/Alert';
import DnsInstructionsModal from './DnsInstructionsModal';

// Definição da interface do domínio
interface Domain {
    id: string;
    companyId: string;
    domain: string;
    verified: boolean;
    active: boolean;
    cnameRecord: string;
    lastVerifiedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export default function DomainsTable() {
    const navigate = useNavigate();
    const [domains, setDomains] = useState<Domain[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [alertInfo, setAlertInfo] = useState<{
        show: boolean;
        type: "success" | "error" | "warning" | "info";
        message: string;
        title: string;
    } | null>(null);

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [domainToDelete, setDomainToDelete] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

    const [verifyModalOpen, setVerifyModalOpen] = useState<boolean>(false);
    const [domainToVerify, setDomainToVerify] = useState<string | null>(null);
    const [verifyLoading, setVerifyLoading] = useState<boolean>(false);

    const [dnsInstructionsModalOpen, setDnsInstructionsModalOpen] = useState<boolean>(false);
    const [selectedDomainForInstructions, setSelectedDomainForInstructions] = useState<Domain | null>(null);

    useEffect(() => {
        fetchDomains();
    }, []);

    const fetchDomains = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await api.get('/api/tracking-domains', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });

            if (response.data && response.data.success) {
                setDomains(response.data.data);
            } else {
                setDomains([]);
            }
        } catch (err: any) {
            console.error("Erro ao buscar domínios:", err);
            setError(err.message || "Erro ao carregar domínios");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyDomain = async () => {
        if (!domainToVerify) return;

        setVerifyLoading(true);
        try {
            const response = await api.post(
                `/api/tracking-domains/${domainToVerify}/verify`,
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            setVerifyModalOpen(false);
            setDomainToVerify(null);
            setVerifyLoading(false);

            if (response.data.success && response.data.data.verified) {
                setAlertInfo({
                    show: true,
                    type: "success",
                    title: "Sucesso!",
                    message: "Domínio verificado com sucesso!"
                });
            } else {
                setAlertInfo({
                    show: true,
                    type: "error",
                    title: "Verificação falhou",
                    message: response.data.message || "Os registros DNS ainda não foram configurados corretamente."
                });
            }

            fetchDomains();
            setTimeout(() => setAlertInfo(null), 5000);
        } catch (error: any) {
            console.error("Erro ao verificar domínio:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: error.response?.data?.message || "Erro ao verificar domínio. Tente novamente."
            });
            setVerifyLoading(false);
            setTimeout(() => setAlertInfo(null), 5000);
        }
    };

    const handleDeleteDomain = async () => {
        if (!domainToDelete) return;

        setDeleteLoading(true);
        try {
            await api.delete(
                `/api/tracking-domains/${domainToDelete}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            setDeleteModalOpen(false);
            setDomainToDelete(null);
            setDeleteLoading(false);

            fetchDomains();

            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: "Domínio excluído com sucesso."
            });

            setTimeout(() => setAlertInfo(null), 3000);
        } catch (error) {
            console.error("Erro ao excluir domínio:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao excluir domínio. Tente novamente."
            });
            setDeleteLoading(false);
        }
    };

    const handleToggleActive = async (domainId: string, currentStatus: boolean) => {
        try {
            await api.patch(
                `/api/tracking-domains/${domainId}/toggle`,
                { active: !currentStatus },
                {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                }
            );

            setAlertInfo({
                show: true,
                type: "success",
                title: "Sucesso!",
                message: `Domínio ${!currentStatus ? 'ativado' : 'desativado'} com sucesso.`
            });

            fetchDomains();
            setTimeout(() => setAlertInfo(null), 3000);
        } catch (error) {
            console.error("Erro ao alterar status do domínio:", error);
            setAlertInfo({
                show: true,
                type: "error",
                title: "Erro!",
                message: "Erro ao alterar status do domínio."
            });
        }
    };

    const openVerifyModal = (domainId: string) => {
        setDomainToVerify(domainId);
        setVerifyModalOpen(true);
    };

    const openDeleteModal = (domainId: string) => {
        setDomainToDelete(domainId);
        setDeleteModalOpen(true);
    };

    const openDnsInstructionsModal = (domain: Domain) => {
        setSelectedDomainForInstructions(domain);
        setDnsInstructionsModalOpen(true);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = domains.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(domains.length / itemsPerPage);

    if (error) {
        return <div className="p-4 text-center dark:text-white/90">Erro: {error}</div>;
    }

    if (domains.length === 0 && !loading) {
        return (
            <ComponentCard title="Gerencie seus Domínios de Tracking">
                <div className="flex flex-col items-center justify-center">
                    <div className="p-8 text-center dark:text-white/90">
                        <p className="mb-2">Você ainda não possui domínios cadastrados.</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Adicione domínios personalizados para melhorar o tracking dos seus pixels.
                        </p>
                    </div>
                    <Button
                        size="md"
                        variant="primary"
                        onClick={() => navigate(`/apps/domains/new`)}
                    >
                        Adicionar Domínio
                    </Button>
                </div>
            </ComponentCard>
        );
    }

    return (
        <ComponentCard title="Gerencie seus Domínios de Tracking">
            <div className="col-span-12 space-y-6">
                <div className="mb-6">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="relative w-full md:w-1/3 lg:w-1/4">
                            <button className="absolute -translate-y-1/2 left-4 top-1/2">
                                <svg
                                    className="fill-gray-500 dark:fill-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                                        fill=""
                                    />
                                </svg>
                            </button>
                            <input
                                type="text"
                                placeholder="Pesquisar domínio..."
                                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-4 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                            />
                        </div>

                        <Button
                            size="md"
                            variant="primary"
                            onClick={() => navigate(`/apps/domains/new`)}
                        >
                            Adicionar Domínio
                        </Button>
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-xl">
                <div className="max-w-full overflow-x-auto">
                    <Table>
                        <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                            <TableRow>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Status
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Domínio
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Verificação
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                                >
                                    Última Verificação
                                </TableCell>
                                <TableCell
                                    isHeader
                                    className="px-5 py-3 font-medium text-gray-500 text-end text-theme-xs dark:text-gray-400 pr-8"
                                >
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHeader>

                        <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                            {!loading && currentItems.map((domain) => (
                                <TableRow key={domain.id}>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={domain.active ? "success" : "error"}
                                        >
                                            {domain.active ? "Ativo" : "Inativo"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div>
                                            <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                                {domain.domain}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                tracking.{domain.domain}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <Badge
                                            size="sm"
                                            color={domain.verified ? "success" : "warning"}
                                        >
                                            {domain.verified ? (
                                                <span className="flex items-center gap-1">
                                                    <CheckCircle className="size-3" />
                                                    Verificado
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-1">
                                                    <XCircle className="size-3" />
                                                    Não Verificado
                                                </span>
                                            )}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {domain.lastVerifiedAt ? (
                                            new Date(domain.lastVerifiedAt).toLocaleString('pt-BR')
                                        ) : (
                                            <span className="text-gray-400 italic">Nunca verificado</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-gray-500 text-end text-theme-sm dark:text-gray-400 pr-8">
                                        <div className="flex justify-end gap-2 relative">
                                            <MoreButton
                                                options={[
                                                    // Mostrar "Ver instruções DNS" e "Verificar DNS" apenas se NÃO estiver verificado
                                                    ...(!domain.verified ? [
                                                        {
                                                            label: "Ver instruções DNS",
                                                            onClick: () => openDnsInstructionsModal(domain),
                                                            icon: <Copy className="size-4" />
                                                        },
                                                        {
                                                            label: "Verificar DNS",
                                                            onClick: () => openVerifyModal(domain.id),
                                                            icon: <RefreshCw className="size-4" />
                                                        }
                                                    ] : []),
                                                    {
                                                        label: domain.active ? "Desativar" : "Ativar",
                                                        onClick: () => handleToggleActive(domain.id, domain.active),
                                                        icon: domain.active ? <XCircle className="size-4" /> : <CheckCircle className="size-4" />
                                                    },
                                                    {
                                                        label: "Excluir",
                                                        onClick: () => openDeleteModal(domain.id),
                                                        icon: <Trash2 className="size-4" />,
                                                        className: "text-red-600 dark:text-red-400"
                                                    }
                                                ]}
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {!loading && totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {alertInfo && alertInfo.show && (
                <div className="mt-4">
                    <Alert
                        variant={alertInfo.type}
                        title={alertInfo.title}
                        message={alertInfo.message}
                    />
                </div>
            )}

            {/* Modal de confirmação de exclusão */}
            <Modal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                size="sm"
                onConfirm={handleDeleteDomain}
                confirmText="Excluir"
                cancelText="Cancelar"
                confirmButtonClass="bg-red-500 hover:bg-red-600 text-white"
                confirmLoading={deleteLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tem certeza que deseja excluir este domínio?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Não será possível reverter esta ação!
                    </p>
                </div>
            </Modal>

            {/* Modal de verificação de domínio */}
            <Modal
                isOpen={verifyModalOpen}
                onClose={() => setVerifyModalOpen(false)}
                size="sm"
                onConfirm={handleVerifyDomain}
                confirmText="Verificar"
                cancelText="Cancelar"
                confirmButtonClass="bg-blue-500 hover:bg-blue-600 text-white"
                confirmLoading={verifyLoading}
            >
                <div className="flex flex-col items-center text-center py-4">
                    <RefreshCw className="h-16 w-16 text-blue-500 mb-4" />
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Verificar configuração DNS?
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                        Vamos verificar se os registros DNS foram configurados corretamente.
                    </p>
                </div>
            </Modal>

            {/* Modal de instruções DNS */}
            {selectedDomainForInstructions && (
                <DnsInstructionsModal
                    isOpen={dnsInstructionsModalOpen}
                    onClose={() => {
                        setDnsInstructionsModalOpen(false);
                        setSelectedDomainForInstructions(null);
                    }}
                    domain={selectedDomainForInstructions}
                    onVerify={() => {
                        setDnsInstructionsModalOpen(false);
                        openVerifyModal(selectedDomainForInstructions.id);
                    }}
                />
            )}
        </ComponentCard>
    );
}
